import { Controller } from "@hotwired/stimulus";
import { visitPreferredLanguageSettings } from "../../Shared/Localization/PreferredLanguageNavigation";

export default class AddLanguagesController extends Controller<HTMLElement> {
    static targets = ["checkbox", "saveButton", "form", "status"];
    static values = { initial: Array, error: String };
    declare readonly checkboxTargets: HTMLInputElement[];
    declare readonly saveButtonTarget: HTMLButtonElement;
    declare readonly formTarget: HTMLFormElement;
    declare readonly statusTarget: HTMLElement;
    declare readonly hasStatusTarget: boolean;
    declare readonly initialValue: string[];
    declare readonly errorValue: string;
    private selected: string[] = [];
    private submitting = false;
    private abort?: AbortController;
    private readonly discardDraft = (): void => {
        this.selected = [...this.initialValue];
        this.restoreFields();
        for (const checkbox of this.checkboxTargets) checkbox.checked = this.selected.includes(checkbox.value);
        this.submitting = false;
        this.update();
    };

    connect(): void { this.discardDraft(); document.addEventListener("turbo:before-cache", this.discardDraft); }
    disconnect(): void { this.abort?.abort(); document.removeEventListener("turbo:before-cache", this.discardDraft); }

    selectionChanged(event: Event): void {
        const checkbox = event.target as HTMLInputElement;
        this.selected = checkbox.checked ? [...this.selected.filter(value => value !== checkbox.value), checkbox.value] : this.selected.filter(value => value !== checkbox.value);
        this.update();
    }

    async prepareSubmit(event: Event): Promise<void> {
        event.preventDefault();
        if (this.saveButtonTarget.disabled || this.submitting) return;
        this.submitting = true;
        for (const checkbox of this.checkboxTargets) checkbox.removeAttribute("name");
        for (const value of this.selected) {
            const field = document.createElement("input"); field.type = "hidden"; field.name = "languages"; field.value = value; field.dataset.selectedLanguage = "";
            this.formTarget.append(field);
        }
        this.update();
        this.abort = new AbortController();
        try {
            const response = await fetch(this.formTarget.action, { method: "POST", body: new FormData(this.formTarget), credentials: "same-origin", signal: this.abort.signal });
            if (!response.ok) throw new Error(String(response.status));
            if (!this.abort.signal.aborted) visitPreferredLanguageSettings();
        } catch {
            if (!this.abort.signal.aborted && this.hasStatusTarget) this.statusTarget.textContent = this.errorValue;
        } finally {
            if (!this.abort.signal.aborted) { this.submitting = false; this.restoreFields(); this.update(); }
        }
    }

    private restoreFields(): void {
        this.formTarget.querySelectorAll("[data-selected-language]").forEach(field => field.remove());
        this.checkboxTargets.forEach(checkbox => checkbox.name = "languages");
    }

    private update(): void {
        const unchanged = this.selected.length === this.initialValue.length && this.selected.every(value => this.initialValue.includes(value));
        this.saveButtonTarget.disabled = this.submitting || this.selected.length === 0 || unchanged;
    }
}
